import { useState, type FormEvent } from "react";
import { Copy, KeyRound, Plus, Trash2 } from "lucide-react";
import { Field, Input } from "../../components/Form";
import { Empty, ErrorState, Failure, Loading, Success } from "../../components/State";
import { api, json } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";

interface AdminAgentKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  status: "active" | "revoked" | "expired";
  expires_at: string;
  last_used_at?: string;
  revoked_at?: string;
  created_at: string;
}

type CreatedAdminAgentKey = AdminAgentKey & { key: string };

const adminScopes = ["reviews:read", "reviews:decide"];

export function AdminMCPKeysPage() {
  const result = useAsync(() => api<{ items: AdminAgentKey[] }>("/admin/agent-keys"), []);
  const [name, setName] = useState("园区审批 MCP");
  const [days, setDays] = useState(30);
  const [scopes, setScopes] = useState(adminScopes);
  const [created, setCreated] = useState<CreatedAdminAgentKey>();
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const endpoint = `${window.location.origin}/admin/mcp`;

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setMessage("已复制");
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy("create"); setError(""); setMessage("");
    try {
      const value = await api<CreatedAdminAgentKey>("/admin/agent-keys", {
        method: "POST", body: json({ name, scopes, expiresInDays: days }),
      });
      setCreated(value);
      setMessage("管理员 MCP Key 已签发");
      await result.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "无法签发管理员 MCP Key");
    } finally { setBusy(""); }
  };

  const revoke = async (item: AdminAgentKey) => {
    if (!window.confirm(`撤销“${item.name}”？使用该 Key 的管理员 MCP 将立即失效。`)) return;
    setBusy(item.id); setError(""); setMessage("");
    try {
      await api<void>(`/admin/agent-keys/${item.id}`, { method: "DELETE" });
      if (created?.id === item.id) setCreated(undefined);
      setMessage("管理员 MCP Key 已撤销");
      await result.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "无法撤销管理员 MCP Key");
    } finally { setBusy(""); }
  };

  return <div>
    <header className="admin-page-header"><div><span>ADMIN MCP ACCESS</span><h1>管理员 MCP</h1><p>为园区审批客户端签发独立凭据，并控制查询和审批权限。</p></div></header>
    <section className="admin-panel agent-key-card">
      <header><span className="security-icon agent"><KeyRound size={20} /></span><div><h2>连接与凭据</h2><p>管理员 Key 仅适用于独立审批端点，不可用于普通用户 MCP。</p></div></header>
      {message && <Success>{message}</Success>}{error && <Failure>{error}</Failure>}
      <Field label="管理员 MCP 端点"><div className="copy-row"><Input readOnly value={endpoint}/><button className="icon-button" type="button" title="复制管理员 MCP 端点" onClick={() => void copy(endpoint)}><Copy size={16}/></button></div></Field>
      {created && <div className="agent-key-reveal"><strong>仅显示一次</strong><div className="copy-row"><Input readOnly value={created.key}/><button className="icon-button" type="button" title="复制管理员 MCP Key" onClick={() => void copy(created.key)}><Copy size={16}/></button></div></div>}
      <form className="agent-key-create" onSubmit={create}>
        <div className="form-grid cols-2"><Field label="Key 名称" required><Input required maxLength={80} value={name} onChange={(event) => setName(event.target.value)}/></Field><Field label="有效天数" required><Input required type="number" min={1} max={90} value={days} onChange={(event) => setDays(Number(event.target.value))}/></Field></div>
        <div className="agent-scope-options">{adminScopes.map((scope) => <label key={scope}><input type="checkbox" checked={scopes.includes(scope)} onChange={(event) => setScopes(event.target.checked ? [...scopes, scope] : scopes.filter((item) => item !== scope))}/><span>{scope}</span></label>)}</div>
        <button className="primary-button" disabled={busy !== "" || !scopes.length}><Plus size={16}/>{busy === "create" ? "签发中..." : "签发新 Key"}</button>
      </form>
      {result.loading ? <Loading label="正在读取管理员 MCP Key"/> : result.error ? <ErrorState error={result.error} retry={result.retry}/> : !result.data?.items.length ? <Empty title="还没有管理员 MCP Key"/> : <div className="agent-key-list">{result.data.items.map((item) => <article key={item.id}><div><strong>{item.name}</strong><small>{item.prefix} · 到期 {new Date(item.expires_at).toLocaleString("zh-CN")}</small><span>{item.scopes.join(" · ")}</span></div><em className={`badge ${item.status === "active" ? "success" : "neutral"}`}>{item.status === "active" ? "有效" : item.status === "revoked" ? "已撤销" : "已过期"}</em>{item.status === "active" && <button className="danger-button" disabled={busy !== ""} onClick={() => void revoke(item)}><Trash2 size={15}/>撤销</button>}</article>)}</div>}
    </section>
  </div>;
}
